/* eslint-disable */
import { withStyles } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import CheckIcon from '@material-ui/icons/Check';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import get from 'lodash/get';
import React, { useReducer, useRef } from 'react';
import compose from 'recompose/compose';
import isFunction from '../../Support/Is/Function/IsFunction';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import Form from '../Form/Form.component';
import FileUploadPropTypes from './FileUpload.component.props';
import fileUploadStyles from './FileUpload.component.styles';

const FILE_UPLOAD_UPLOADING = `[FILE UPLOAD] UPLOADING`;
const FILE_UPLOAD_ON_FILE_SELECTED = `[FILE UPLOAD] ON FILE SELECTED`;
const FILE_UPLOAD_SUCCESS = `[FILE UPLOAD] SUCCESS`;
const FILE_UPLOAD_FAIL = `[FILE UPLOAD] FAIL`;
const FILE_UPLOAD_RESET = `[FILE UPLOAD] RESET`;

const initialUploadState = {
  canUpload: false,
  result: null,
  uploading: false,
};

/**
 * uploadReducer
 * @param {any} state The state
 * @param {any} action The action
 * @returns {any} The new state
 */
const uploadReducer = (state, action) => {
  switch (action.type) {
    case FILE_UPLOAD_UPLOADING: {
      return {
        uploading: true,
      };
    }
    case FILE_UPLOAD_ON_FILE_SELECTED: {
      return {
        canUpload: true,
        result: null,
        uploading: false,
      };
    }
    case FILE_UPLOAD_SUCCESS: {
      return {
        canUpload: false,
        result: true,
        uploading: false,
      };
    }
    case FILE_UPLOAD_FAIL: {
      return {
        canUpload: false,
        errors: action.errors,
        result: false,
        uploading: false,
      };
    }
    case FILE_UPLOAD_RESET: {
      return {
        canUpload: false,
        errors: undefined,
        result: null,
        uploading: false,
      };
    }
    default:
      throw new Error(`FileUpload type ${action.type} not recognized`);
  }
};

/**
 * uploadFiles
 * @param {any} dispatch The dispatch
 * @returns {React.Component} The component
 */
const uploadFiles = (dispatch) => async ({ files, headers, method, url }) => {
  try {
    const formData = new FormData();
    formData.append(`file`, files);
    dispatch({ type: FILE_UPLOAD_UPLOADING });
    const response = await method(url, {
      data: formData,
      headers: {
        ...headers,
        'Content-Type': `application/x-www-form-urlencoded`,
      },
    });
    dispatch({ payload: response, type: FILE_UPLOAD_SUCCESS });
    return response;
  } catch (err) {
    dispatch({ errors: err.message, type: FILE_UPLOAD_FAIL });
  }
};

/* eslint-disable react/prop-types */

/**
 * SubmitIcon
 * @param {any} props The props
 * @returns {React.Component} The component
 */
const SubmitIcon = ({ result }) => {
  if (result === null) {
    return <CloudUploadIcon />;
  }
  return result ? <CheckIcon /> : <ErrorOutlineIcon />;
};

/* eslint-enable react/prop-types */

/**
 * FileUploadComponent
 * @param {any} props The props
 * @returns {React.Component} The component
 */
const FileUploadComponent = ({ inputProps, onChange, onSuccess, renderErrors, uploadProps }) => {
  const [state, dispatch] = useReducer(uploadReducer, initialUploadState);
  const { canUpload, errors, result, uploading } = state;
  const inputRef = useRef();
  const uploader = uploadFiles(dispatch);
  return (
    <Form
      onSubmit={async () => {
        const { headers, method, url } = uploadProps;
        const [file] = get(inputRef, `current.files`);
        const response = await uploader({
          files: file,
          headers,
          method,
          url,
        });
        if (isFunction(onSuccess)) {
          onSuccess(response);
        }
      }}
    >
      <TextField
        disabled={uploading}
        error={result === false}
        fullWidth
        helperText={result === false && renderErrors(errors)}
        id="file-input"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="Upload file" color="primary" disabled={!canUpload} onClick={(e) => e.stopPropagation()} type="submit">
                {uploading ? <CircularProgress size={25} /> : <SubmitIcon result={result} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        /* eslint-disable-next-line react/jsx-no-duplicate-props */
        inputProps={inputProps}
        inputRef={inputRef}
        onChange={() => {
          dispatch({ type: FILE_UPLOAD_ON_FILE_SELECTED });
          if (isFunction(onChange)) {
            onChange();
          }
        }}
        onClick={() => {
          inputRef.current.value = ``;
        }}
        type="file"
        variant="outlined"
      />
    </Form>
  );
};

FileUploadComponent.propTypes = FileUploadPropTypes;

const FileUpload = compose(withStyles(themeCreateInjectableStyles(fileUploadStyles), { withTheme: true }))(FileUploadComponent);

export default FileUpload;
